<template>
    <v-dialog v-model="dialog" fullscreen persistent transition="dialog-bottom-transition">
        <div class="timer-container">
            <brand-backdrop />
            <v-progress-circular class="timer-ring" :size="250" :width="10" :rotate="isTimeCircuit ? 0 : -90"
                :value="progressValue" :indeterminate="isTimeCircuit" :color="progressColor" />
            <div class="timer-text">{{ formattedTime }}</div>
            <template v-if="isTabata">
                <div class="timer-laps">
                    {{ $t('common.lap') }} <br> {{ currentMinute }}/{{ tabata.minutes }}
                </div>
                <div class="timer-sets">
                    {{ $tc('exercises.sets', 1) }} <br> {{ currentSeries }}/{{ tabata.sets }}
                </div>
            </template>
            <template v-if="isEmom">
                <div class="timer-laps">
                    {{ currentMinute }}'/{{ duration / 60 }}'
                </div>
            </template>
            <v-btn v-if="isTimeCircuit || closable" class="timer-btn normal-btn black--text" @click="hide"
                color="primary" depressed>
                {{ $t('common.finish_timer') }}
            </v-btn>
            <v-btn v-if="pausable || isTabata || isAmrap || isEmom" class="timer-btn normal-btn black--text"
                @click="togglePause" color="primary" depressed>
                {{ isPaused ? $t('common.resume') : $t('common.pause') }}
            </v-btn>
            <v-btn v-if="pausable || isTabata || isAmrap || isEmom" class="close-btn" @click="closeTimer"
                color="#FFFFFF" depressed fab outlined small>
                <v-icon>mdi-close</v-icon>
            </v-btn>
        </div>
    </v-dialog>
</template>

<script>
import BrandBackdrop from '@/components/common/BrandBackdrop'

export default {
    components: {
        BrandBackdrop,
    },
    props: {
        duration: {
            type: Number,
            default: 60,
        },
        type: {
            type: String,
            default: null,
        },
        tabata: {
            type: Object,
            default: null
        },
        closable: {
            type: Boolean,
            default: false,
        },
        infinite: {
            type: Boolean,
            default: false,
        },
        pausable: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            dialog: false,
            timeElapsed: 0,
            currentSeries: 1,
            currentMinute: 1,
            isWorking: true,
            isSetPaused: false,
            isPaused: false,
            sumSecondsInterval: null,
            timeInCurrentPhase: 0,
            originalDuration: 0,
        };
    },
    computed: {
        progressValue() {
            if (this.infinite) return 100;
            if (this.isTimeCircuit) return 100;
            if (this.isTabata) {
                if (this.isSetPaused) return (this.timeInCurrentPhase / this.tabata.timePause) * 100;
                const totalPhaseTime = this.isWorking ? this.tabata.timeWork : this.tabata.timeRest;
                return (this.timeInCurrentPhase / totalPhaseTime) * 100;
            }
            if (this.isEmom) {
                const secondsPassed = this.timeElapsed % 60;
                return (secondsPassed / 60) * 100;
            }
            return (this.timeElapsed / this.duration) * 100;
        },
        formattedTime() {
            const minutes = Math.floor(this.timeElapsed / 60);
            const seconds = Math.floor(this.timeElapsed % 60);
            return `${minutes < 10 ? "0" : ""}${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
        },
        isTimeCircuit() {
            return this.type === 'time';
        },
        isTabata() {
            return this.type === 'tabata';
        },
        isAmrap() {
            return this.type === 'amrap';
        },
        isEmom() {
            return this.type === 'emom';
        },
        progressColor() {
            if (this.isSetPaused) return 'error'
            return this.isWorking ? 'primary' : 'secondary';
        }
    },
    methods: {
        show() {
            this.dialog = true;
            if (!this.isPaused) this.startTimer();
        },
        hide() {
            this.dialog = false;
            this.$emit("hide");
            clearInterval(this.sumSecondsInterval);
        },
        reset() {
            this.timeElapsed = this.isAmrap ? this.duration : 0;
            this.currentSeries = 1;
            this.currentMinute = 1;
            this.isWorking = true;
            this.timeInCurrentPhase = 0;
            this.originalDuration = this.duration;
        },
        togglePause() {
            this.isPaused = !this.isPaused;
        },
        closeTimer() {
            this.dialog = false;
            if (!this.isPaused) {
                clearInterval(this.sumSecondsInterval);
                this.reset()
            }
        },
        startTimer() {
            this.reset()
            this.sumSecondsInterval = setInterval(() => {
                if (this.isPaused) return;
                if (this.isTabata) {
                    this.handleTabataTimer();
                } else if (this.isTimeCircuit || this.infinite) {
                    this.timeElapsed++;
                } else {
                    if (this.isAmrap) {
                        if (this.timeElapsed > 0) {
                            this.timeElapsed--;
                        } else {
                            this.playBeep();
                            this.hide();
                        }
                        return;
                    }

                    if (this.timeElapsed < this.duration) {
                        this.timeElapsed++;
                        if (this.isEmom && this.timeElapsed % 60 === 0) {
                            this.playBeep();
                        }
                    } else {
                        if (this.isAmrap) this.playBeep();
                        this.hide();
                    }
                }
            }, 1000);
        },
        handleTabataTimer() {
            this.timeInCurrentPhase++;
            this.timeElapsed++;

            if (this.isSetPaused) {
                if (this.timeInCurrentPhase >= this.tabata.timePause) {
                    this.playBeep();
                    this.isSetPaused = false;
                    this.isWorking = true;
                    this.timeInCurrentPhase = 0;
                    this.timeElapsed = 0;
                    this.currentMinute = 1;
                }
                return;
            }

            const totalPhaseTime = this.isWorking ? this.tabata.timeWork : this.tabata.timeRest;

            if (this.timeInCurrentPhase >= totalPhaseTime) {
                if (!this.isWorking && this.currentMinute === this.tabata.minutes) {
                    this.endMinute();
                } else {
                    this.switchPhase();
                }
            }
        },
        switchPhase() {
            this.playBeep();
            this.isWorking = !this.isWorking;
            this.timeInCurrentPhase = 0;
            this.timeElapsed = 0;
            if (this.isWorking && !this.isSetPaused) this.currentMinute++;
        },
        endMinute() {
            if (this.currentSeries >= this.tabata.sets) {
                this.playBeep();
                this.hide();
            } else {
                this.playBeep();
                this.currentSeries++;
                this.isSetPaused = true;
                this.timeInCurrentPhase = 0;
                this.timeElapsed = 0;
            }
        },
        playBeep() {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.type = "sine";
            oscillator.frequency.setValueAtTime(1000, audioContext.currentTime);

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            gainNode.gain.setValueAtTime(1, audioContext.currentTime);
            oscillator.start();

            setTimeout(() => {
                oscillator.stop();
                audioContext.close();
            }, 200);
        }
    },
    beforeDestroy() {
        clearInterval(this.sumSecondsInterval);
    },
};
</script>

<style scoped>
.timer-container {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    overflow: hidden;
    color: #FFFFFF;
    background: #06080C;
    height: 100%;
}

.timer-ring {
    position: relative;
    z-index: 1;
}

.timer-text {
    position: absolute;
    z-index: 1;
    font-size: 56px;
    font-weight: 700;
    letter-spacing: 1px;
    font-variant-numeric: tabular-nums;
}

.timer-laps {
    position: absolute;
    top: calc(50% + -80px);
    bottom: 0;
    margin: auto;
    height: auto;
    text-align: center;
    font-size: 20px;
    line-height: 22px;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 2px;
}

.timer-sets {
    position: absolute;
    top: calc(50% + 40px);
    bottom: 0;
    margin: auto;
    height: auto;
    text-align: center;
    font-size: 20px;
    line-height: 22px;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 2px;
}

.timer-btn {
    position: absolute;
    top: 350px;
    bottom: 0;
    margin: auto;
    height: auto;
}

.close-btn {
    position: absolute;
    top: 550px;
    bottom: 0;
    margin: auto;
}
</style>
